import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { usePortfolioStateContext } from 'Components/UI/Portfolio/PortfolioContext';
import CircleChart from 'Components/UI/Chart/CircleChart';
import RingWithText from 'Components/UI/Chart/RingWithText';
import _ from 'lodash';
import Typography from '@material-ui/core/Typography';
import { color as d3Color } from 'd3';
import Button from '@material-ui/core/Button';
import CancelIcon from '@material-ui/icons/Cancel';
import { currency } from 'Helpers/Formats';
import ChartLegend from './ChartLegend';

const getTooltip = resource => `
${resource.attr('label')} - ${currency(
  parseFloat(resource.attr('value')),
  false,
)}

Percentage of Portfolio - ${resource.attr('percentageValue')}`;

const getTransparent = (c) => {
  const {
    r, g, b, opacity,
  } = d3Color(c);
  return `rgba(${r},${g},${b},${opacity * 0.35})`;
};

const NoHoldings = ({ classes }) => {
  return (
    <div className={classes.noHoldings}>
      <Typography color="primary" variant="h4" component="p">
        No holdings to display.
      </Typography>
    </div>
  );
};

NoHoldings.propTypes = {
  classes: PropTypes.object.isRequired,
};

export const useChartPercentages = (array = []) => React.useMemo(() => {
  const data = array.reduce(
    (root, next) => {
      const nextEquityPercent = root.equityPercent;
      const nextFixedPercent = root.fixedPercent;
      const nextUnclassifiedPercent = root.unclassifiedPercent;
      switch (next.resourceId) {
        case 'Stocks': {
          nextEquityPercent.color = next.attr('color');
          nextEquityPercent.value += parseFloat(next.attr('percentageValue'));
          break;
        }
        case 'Cash':
        case 'Bonds': {
          nextFixedPercent.color = next.attr('color');
          nextFixedPercent.value += parseFloat(next.attr('percentageValue'));
          break;
        }
        default: {
          nextUnclassifiedPercent.color = next.attr('color');
          nextUnclassifiedPercent.value += parseFloat(
            next.attr('percentageValue'),
          );
          break;
        }
      }

      return {
        equityPercent: nextEquityPercent,
        fixedPercent: nextFixedPercent,
        unclassifiedPercent: nextUnclassifiedPercent,
      };
    },
    {
      equityPercent: { color: '#EEE', value: 0 },
      fixedPercent: { color: '#EEE', value: 0 },
      unclassifiedPercent: { color: '#EEE', value: 0 },
    },
    [array],
  );

  return {
    equityPercent: {
      ...data.equityPercent,
      value: `${data.equityPercent.value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`,
    },
    fixedPercent: {
      ...data.fixedPercent,
      value: `${data.fixedPercent.value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`,
    },
    unclassifiedPercent: {
      ...data.unclassifiedPercent,
      value: `${data.unclassifiedPercent.value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`,
    },
  };
});

export const getChartData = (portfolio, selectedCategory, withTooltip) => {
  return _(portfolio?.attr('categoryChartData'))
    .toArray()
    .orderBy(a => parseFloat(a.attr('percentageValue')), ['desc'])
    .map((resource) => {
      const label = resource.attr('label');
      const type = resource.attr('type');
      let color = resource.attr('color');
      const percentageValue = resource.attr('percentageValue');

      if (selectedCategory && selectedCategory !== label) {
        color = getTransparent(color);
      }

      return {
        tooltip: withTooltip ? getTooltip(resource) : null,
        value: resource.attr('value'),
        color,
        type,
        label,
        percentageValue,
      };
    })
    .value();
};

export const getTextFields = (
  { unclassifiedPercent, fixedPercent, equityPercent },
  selected,
) => {
  if (selected) {
    return [
      {
        text: `${selected.attr('percentageValue')} ${selected.attr('label')}`,
        props: {
          fill: selected.attr('color'),
          style: { fontSize: 14, cursor: 'pointer', underline: 'true' },
        },
      },
    ];
  }
  const textFields = [
    {
      text: `${equityPercent.value}% Equity`,
      props: {
        fill: equityPercent.color,
        style: { fontSize: 14 },
      },
    },
    {
      text: `${fixedPercent.value}% Fixed`,
      props: {
        fill: fixedPercent.color,
        style: { fontSize: 14 },
      },
    },
  ];

  if (unclassifiedPercent.value !== '0') {
    textFields.push({
      text: `${unclassifiedPercent.value}% Other`,
      props: {
        fill: unclassifiedPercent.color,
        style: { fontSize: 14 },
      },
    });
  }

  return textFields;
};

function Chart({ classes, className, center }) {
  const { portfolioCollection } = usePortfolioStateContext();
  const portfolio = portfolioCollection?.resources().head();
  const [selectedCategory, setSelectedCategory] = React.useState();
  const [showLegend, setShowLegend] = React.useState(false);
  const assetChartDataArray = _(portfolio?.attr('assetClassChartData'))
    .toArray()
    .value();
  const percentages = useChartPercentages(assetChartDataArray, selectedCategory);
  const categoryChartData = getChartData(portfolio, selectedCategory, true);

  const textFields = getTextFields(
    percentages,
    selectedCategory
      && _(portfolio?.attr('categoryChartData'))
        .toArray()
        .find(d => d.attr('label') === selectedCategory),
  );

  if (portfolioCollection && !categoryChartData.length) {
    return <NoHoldings classes={classes} />;
  }
  const toggleLegend = () => setShowLegend(!showLegend);

  return (
    <div className={classNames(classes.root, className)}>
      <CircleChart
        ChartComponent={RingWithText}
        showSkeleton={!portfolio}
        ChartProps={{
          animate: false,
          selectedCategory,
          onSegmentClick: e => {
            const {
              data: { label },
            } = e;
            setSelectedCategory(label === selectedCategory ? null : label);
          },
          align: center ? 'center' : 'left',
          holdings: portfolio,
          data: categoryChartData,
          title: 'Allocations',
          titleProps: {
            style: { fontSize: 18 },
          },
          textOffset: 18,
          textFields,
        }}
        classes={{ wrapper: classes.chartWrapper }}
      />
      {selectedCategory ? (
        <Button
          startIcon={<CancelIcon size="small" />}
          color="primary"
          onClick={() => setSelectedCategory()}
        >
          Clear Filters
        </Button>
      ) : (
        <Button onClick={toggleLegend} color="primary">
          {showLegend ? 'Hide Details' : 'Show All Details'}
        </Button>
      )}

      {(showLegend || selectedCategory) && (
        <ChartLegend
          sections={
            selectedCategory
              ? categoryChartData.filter(d => d.label === selectedCategory)
              : categoryChartData
          }
          accountCollection={portfolio?.attr('accounts')}
        />
      )}
    </div>
  );
}

Chart.defaultProps = {
  className: '',
  center: false,
};

Chart.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  center: PropTypes.bool,
};

export default withStyles(theme => ({
  root: {
    display: 'flex',
    alignSelf: 'flex-start',
    maxWidth: '100%',
  },
  chartWrapper: {
    minWidth: 300,
    minHeight: 300,
  },
  noHoldings: {
    display: 'flex',
    flex: '1 1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  back: {
    paddingLeft: theme.spacing(2),
    alignSelf: 'flex-start',
  },
}))(Chart);
